import { Tag } from '@carbon/react';
import { type DataTableColumnDef } from 'components/data-table';
import { formatTcWorkspaceDate } from 'utils/dates';
import { format8 } from 'utils/number';
import { fmtAmount, fmtNumber } from '../format';
import styles from './line-item-columns.module.scss';

/**
 * The subset of line-item fields shared between the list expansion
 * (`RoyaltyCheckLineItemRef`) and the show page (`RoyaltyCheckLineItem`).
 * Both row shapes structurally satisfy this interface, so a single set of
 * column definitions can be composed into either context.
 */
export interface SharedLineItemFields {
  productionMonth: string | null;
  productCategory: string | null;
  interestType: string | null;
  lineItemDecimal: number | null;
  grossVolume: number | null;
  grossValue: number | null;
  grossAdjustment: number | null;
  lineItemNetValue: number | null;
  decimalVarianceFlagged?: boolean | null;
}

// Column definitions for the fields common to both line-item tables, keyed by
// column id. Formatting is intentionally canonical here so the two contexts
// can no longer drift: `productionMonth` always renders via
// `formatTcWorkspaceDate` and `lineItemDecimal` always via the precise
// 8-decimal `format8`. Each context spreads these in its own column order and
// interleaves its extra columns.
export const sharedLineItemColumns = <TRow extends SharedLineItemFields>(): {
  productionMonth: DataTableColumnDef<TRow>;
  productCategory: DataTableColumnDef<TRow>;
  interestType: DataTableColumnDef<TRow>;
  lineItemDecimal: DataTableColumnDef<TRow>;
  grossVolume: DataTableColumnDef<TRow>;
  grossValue: DataTableColumnDef<TRow>;
  grossAdjustment: DataTableColumnDef<TRow>;
  lineItemNetValue: DataTableColumnDef<TRow>;
} => ({
  productionMonth: {
    id: 'productionMonth',
    accessorKey: 'productionMonth',
    header: 'Production Month',
    meta: { type: 'date' },
    cell: ({ row }) => {
      const { productionMonth } = row.original;
      return productionMonth ? formatTcWorkspaceDate(productionMonth) : '--';
    },
  },
  productCategory: {
    id: 'productCategory',
    accessorKey: 'productCategory',
    header: 'Product Category',
    cell: ({ row }) => row.original.productCategory ?? '--',
  },
  interestType: {
    id: 'interestType',
    accessorKey: 'interestType',
    header: 'Interest Type',
    cell: ({ row }) => row.original.interestType ?? '--',
  },
  lineItemDecimal: {
    id: 'lineItemDecimal',
    accessorKey: 'lineItemDecimal',
    header: 'Line Item Decimal',
    meta: { type: 'decimal', align: 'right', wrap: true },
    cell: ({ row }) => (
      <div className={styles.decimalCell}>
        {format8(row.original.lineItemDecimal)}
        {row.original.decimalVarianceFlagged && (
          <Tag
            type="red"
            size="sm"
            title="This line item's stated decimal doesn't match its Owner Volume ÷ Gross Volume ratio (variance > 0.001)."
          >
            Decimal variance
          </Tag>
        )}
      </div>
    ),
  },
  grossVolume: {
    id: 'grossVolume',
    accessorKey: 'grossVolume',
    header: 'Gross Volume',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtNumber(row.original.grossVolume),
  },
  grossValue: {
    id: 'grossValue',
    accessorKey: 'grossValue',
    header: 'Gross Value',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.grossValue),
  },
  grossAdjustment: {
    id: 'grossAdjustment',
    accessorKey: 'grossAdjustment',
    header: 'Gross Adjustment',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.grossAdjustment),
  },
  lineItemNetValue: {
    id: 'lineItemNetValue',
    accessorKey: 'lineItemNetValue',
    header: 'Line Item Net Value',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.lineItemNetValue),
  },
});
